import type {
  FontFamiliesResponse,
  FontsResponse,
  FontUploadResponse,
} from "models/types/agency-settings";
import { apiClient } from "utils/api-client";
import { createModel } from "utils/models";

export const FONT_WEIGHT_DESCRIPTORS = {
  100: "Thin",
  200: "Extra Light",
  300: "Light",
  400: "Regular",
  500: "Medium",
  600: "Semi Bold",
  700: "Bold",
  800: "Extra Bold",
  900: "Black",
};

export async function isCantDeleteFontInUseError(
  error: unknown,
): Promise<boolean> {
  if (
    !error ||
    typeof error !== "object" ||
    !("response" in error) ||
    !(error.response instanceof Response)
  ) {
    return false;
  }
  try {
    const response = await error.response.json();
    if (
      response?.message.includes(
        'is still referenced from table "account_brandings"',
      )
    ) {
      return false;
    }
  } catch {
    // noop
  }

  return true;
}

export const fontModel = createModel("fonts", {
  queries: {
    list: () => ({
      queryKey: ["fonts", "list"],
      queryFn: async (): Promise<FontsResponse> => {
        const response = await apiClient.get<FontsResponse>("accounts/fonts");
        return response;
      },
    }),
    listFontFamilies: () => ({
      queryKey: ["fonts", "families"],
      queryFn: async (): Promise<FontFamiliesResponse> => {
        const response = await apiClient.get<FontFamiliesResponse>(
          "accounts/font-families",
        );
        return response;
      },
    }),
  },
  mutations: {
    upload: () => ({
      mutationFn: async ({
        file,
        name,
        weight,
      }: {
        file: File;
        name: string;
        weight: string;
      }) => {
        const response = await apiClient.upload<FontUploadResponse>(
          "accounts/fonts",
          file,
          { name, weight },
        );
        return response;
      },
    }),
    delete: () => ({
      mutationFn: async ({ id }: { id: string }) => {
        await apiClient.delete(`accounts/fonts/${id}`);
      },
      meta: {
        showErrorModal: isCantDeleteFontInUseError,
      },
    }),
  },
});
